import React from "react";
import { 
  Modal, 
  Box, 
  Typography, 
  IconButton, 
  Divider, 
  Grid, 
  Card, 
  CardContent, 
  Chip,
  alpha,
  useTheme,
  Paper
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import ImageIcon from "@mui/icons-material/Image";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import BrushIcon from "@mui/icons-material/Brush";
import FeaturedPlayListIcon from "@mui/icons-material/FeaturedPlayList";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import type { ResourceSection } from "../../../types/resourceSection";

interface ViewSectionModalProps {
  open: boolean;
  onClose: () => void;
  section: ResourceSection | null;
}

const ViewSectionModal: React.FC<ViewSectionModalProps> = ({
  open,
  onClose,
  section,
}) => {
  const theme = useTheme();
  
  if (!section) return null;

  // Helper function to format date strings
  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  };

  // Helper function to render embeddable videos
  const renderVideo = (videoUrl: string) => {
    if (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be')) {
      // Convert to embed URL if needed
      const embedUrl = videoUrl.replace('watch?v=', 'embed/');
      return (
        <Box
          component="iframe"
          src={embedUrl}
          width="100%"
          height="200"
          sx={{ border: 'none', borderRadius: 1 }}
          allowFullScreen
        />
      );
    } 
    if (videoUrl.includes('vimeo.com')) {
      const vimeoId = videoUrl.split('/').pop();
      return (
        <Box
          component="iframe"
          src={`https://player.vimeo.com/video/${vimeoId}`}
          width="100%"
          height="200"
          sx={{ border: 'none', borderRadius: 1 }}
          allowFullScreen
        />
      );
    }
    // Direct video link
    return (
      <Box
        component="video"
        controls
        width="100%"
        height="200"
        sx={{ borderRadius: 1 }}
        src={videoUrl}
      />
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="section-modal-title"
      aria-describedby="section-modal-description"
    >
      <Box
        sx={{
          position: "absolute" as const,
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "90%", sm: "80%", md: "70%" },
          maxWidth: 1000,
          maxHeight: "90vh",
          overflow: "auto",
          bgcolor: "background.paper",
          boxShadow: 24,
          borderRadius: 2,
          p: 4,
        }}
      >
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>

        <Typography id="section-modal-title" variant="h5" component="h2" mb={1} fontWeight="bold">
          {section.mainTitle}
        </Typography>

        {/* Tags display */}
        {section.tags && section.tags.length > 0 && (
          <Box mb={2} display="flex" flexWrap="wrap" gap={1}>
            {section.tags.map((tag, index) => (
              <Chip 
                key={index} 
                icon={<LocalOfferIcon />} 
                label={tag} 
                size="small" 
                sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}
              />
            ))}
          </Box>
        )}

        {/* Cover image display */}
        {section.coverImageUrl && (
          <Box mb={3} textAlign="center">
            <img 
              src={section.coverImageUrl} 
              alt={section.coverImageAlt || section.mainTitle} 
              style={{ 
                maxWidth: '100%', 
                maxHeight: '300px',
                borderRadius: '8px',
                objectFit: 'contain',
              }} 
            />
          </Box>
        )}

        {/* Metadata display */}
        <Box mb={3}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Box display="flex" alignItems="center">
                <BrushIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  Background: <strong>{section.backgroundStyle || 'Light'}</strong>
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box display="flex" alignItems="center">
                <FeaturedPlayListIcon sx={{ mr: 1, color: section.featured ? 'success.main' : 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  Featured: <strong>{section.featured ? 'Yes' : 'No'}</strong>
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box display="flex" alignItems="center">
                <CalendarTodayIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  Created: <strong>{formatDate(section.createdAt)}</strong>
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {section.mainDescription && (
          <Box mb={3}>
            <Typography variant="body1" paragraph>
              {section.mainDescription}
            </Typography>
            <Divider />
          </Box>
        )}

        <Typography variant="h6" gutterBottom fontWeight="medium">
          Subsections ({section.subsections.length})
        </Typography>

        <Grid container spacing={3}>
          {section.subsections.map((subsection, index) => (
            <Grid item xs={12} key={index}>
              <Paper 
                elevation={1} 
                sx={{ 
                  p: 2,
                  backgroundColor: subsection.backgroundColor || '#ffffff',
                  borderLeft: `4px solid ${subsection.accentColor || theme.palette.primary.main}`,
                }}
              >
                <Typography variant="h6" gutterBottom>
                  {subsection.subtitle}
                </Typography>
                
                <Typography variant="body2" paragraph>
                  {subsection.description}
                </Typography>

                {/* Media content */}
                {(subsection.imageUrl || subsection.videoUrl) && (
                  <Box mb={2} mt={2}>
                    <Grid container spacing={2}>
                      {subsection.imageUrl && (
                        <Grid item xs={12} sm={subsection.videoUrl ? 6 : 12}>
                          <Box
                            sx={{ 
                              border: '1px solid #eee', 
                              borderRadius: 1, 
                              p: 1,
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <img 
                              src={subsection.imageUrl} 
                              alt={subsection.imageAlt || 'Subsection image'} 
                              style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain' }} 
                            />
                          </Box>
                        </Grid>
                      )}
                      {subsection.videoUrl && (
                        <Grid item xs={12} sm={subsection.imageUrl ? 6 : 12}>
                          <Box
                            sx={{ 
                              border: '1px solid #eee', 
                              borderRadius: 1, 
                              p: 1 
                            }}
                          >
                            {renderVideo(subsection.videoUrl)}
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                )}

                {/* Bullet points */}
                {subsection.bulletPoints && subsection.bulletPoints.length > 0 && (
                  <Box ml={1}>
                    {subsection.bulletPoints.map((point, pointIndex) => (
                      <Box 
                        key={pointIndex} 
                        display="flex" 
                        alignItems="flex-start" 
                        mb={1}
                      >
                        <CheckCircleIcon 
                          fontSize="small" 
                          sx={{ 
                            mr: 1, 
                            mt: '2px',
                            color: subsection.accentColor || theme.palette.primary.main,
                          }} 
                        />
                        <Typography variant="body2">
                          {point}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Modal>
  );
};

export default ViewSectionModal;
